import { useState, useEffect, useCallback, useRef } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

/**
 * Helper: calculate stats from a sessions list
 */
function calcStats(sessionsList, tasks) {
  let totalDuration = 0;
  let completedSessionCount = 0;
  let completedTasks = 0;
  let totalTasks = 0;
  const taskStats = {};

  sessionsList.forEach((session) => {
    if (session.completedAt && session.startedAt) {
      const start = session.startedAt instanceof Date ? session.startedAt : new Date(session.startedAt);
      const end = session.completedAt instanceof Date ? session.completedAt : new Date(session.completedAt);
      totalDuration += (end - start) / 1000;
      completedSessionCount++;
    }
    if (session.taskResults) {
      session.taskResults.forEach((result) => {
        totalTasks++;
        if (result.status === 'done') completedTasks++;
        if (!taskStats[result.taskId]) taskStats[result.taskId] = { completed: 0, total: 0 };
        taskStats[result.taskId].total++;
        if (result.status === 'done') taskStats[result.taskId].completed++;
      });
    }
  });

  const sessionCount = sessionsList.length;
  const avgDuration = completedSessionCount > 0 ? Math.round(totalDuration / completedSessionCount) : 0;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const taskCompletionRates = (tasks || []).map((task) => {
    const stat = taskStats[task.id] || { completed: 0, total: 0 };
    return {
      taskId: task.id,
      title: task.title,
      completed: stat.completed,
      total: stat.total,
      rate: stat.total > 0 ? Math.round((stat.completed / stat.total) * 100) : 0
    };
  });

  return { sessionCount, avgDuration, completionRate, taskCompletionRates };
}

/**
 * The synthesis doc in the shape the tabs read:
 * { synthesis: { ... }, generatedAt, sessionCount, sessionIds?, totalSessions? }.
 * Very old docs stored the fields flat (executive_summary etc. at the top
 * level); those are nested here.
 */
export function normaliseSynthesisDoc(raw) {
  if (!raw) return null;
  if (raw.synthesis) {
    return { ...raw, generatedAt: raw.generatedAt?.toDate?.() || raw.generatedAt || null };
  }
  const { generatedAt, sessionCount, ...rest } = raw;
  return {
    synthesis: rest,
    generatedAt: generatedAt?.toDate?.() || generatedAt || null,
    sessionCount: sessionCount || 0,
  };
}

/**
 * Hook to fetch combined project data including:
 * - Project metadata
 * - User testing configuration
 * - Testing statistics
 * - Cross-session synthesis
 * - Sessions list (real-time via onSnapshot)
 */
export function useProjectData(userId, projectId) {
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [insightsRun, setInsightsRun] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fetchedRef = useRef(false);
  const unsubSessionsRef = useRef(null);
  const unsubInsightsRef = useRef(null);

  const fetchProjectData = useCallback(async (force = false) => {
    if (!userId || !projectId) {
      setLoading(false);
      return;
    }

    // Skip if already fetched (for instant tab switches)
    if (fetchedRef.current && !force) {
      return;
    }

    try {
      // Only show loading spinner on the initial fetch — not on forced
      // refetches triggered from child tabs (e.g. after synthesis generation).
      // Setting loading=true during a refetch causes the parent component to
      // return null, which unmounts all children and loses their local state.
      if (!force) setLoading(true);
      setError(null);

      const db = firebase.firestore();
      const projectRef = db.collection('user').doc(userId).collection('url').doc(projectId);

      // Fetch project document
      const projectDoc = await projectRef.get();

      if (!projectDoc.exists) {
        setError('Project not found');
        setLoading(false);
        return;
      }

      const projectData = projectDoc.data();

      const projectObj = {
        id: projectDoc.id,
        title: projectData.title || 'Untitled Project',
        type: projectData.type,
        generatedUrl: projectData.generatedUrl,
        // Live deployed version of a site-backed test; compared against
        // userTesting.pinnedVersionId to offer "update test to newest".
        activeVersion: projectData.activeVersion,
        // Test copies (testCopy.js): the site this test was copied from.
        sourceSiteId: projectData.sourceSiteId || null,
        sourceVersionId: projectData.sourceVersionId || null,
        sourceSiteTitle: projectData.sourceSiteTitle || '',
        sourceSiteHost: projectData.sourceSiteHost || '',
        customDomain: projectData.domain,
        userTesting: projectData.userTesting || {},
        createdAt: projectData.createdAt?.toDate?.() || projectData.createdAt,
        updatedAt: projectData.updatedAt?.toDate?.() || projectData.updatedAt
      };

      setProject(projectObj);

      // Insights are built on the server after each analysed session
      // (functions: synthesizeInsightsAfterAnalysis), so the synthesis and
      // its run state are listened to live, like the sessions.
      if (unsubInsightsRef.current) {
        unsubInsightsRef.current();
        unsubInsightsRef.current = null;
      }
      const configRef = projectRef.collection('testingConfig');
      const unsubSynthesis = configRef.doc('synthesis').onSnapshot((doc) => {
        setSynthesis(doc.exists ? normaliseSynthesisDoc(doc.data()) : null);
      }, (err) => {
        console.error('Synthesis listener error:', err);
      });
      const unsubRun = configRef.doc('insightsRun').onSnapshot((doc) => {
        setInsightsRun(doc.exists ? doc.data() : null);
      }, () => {
        // Run state is a nicety; without it the tab still shows the result.
      });
      unsubInsightsRef.current = () => { unsubSynthesis(); unsubRun(); };

      // Unsubscribe previous sessions listener if any
      if (unsubSessionsRef.current) {
        unsubSessionsRef.current();
        unsubSessionsRef.current = null;
      }

      // Set up real-time listener for sessions so transcript/analysis
      // updates from Cloud Functions appear without page refresh
      const tasks = projectData.userTesting?.tasks || [];
      const unsub = projectRef
        .collection('testingSessions')
        .orderBy('startedAt', 'desc')
        .onSnapshot((snapshot) => {
          const sessionsList = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              startedAt: data.startedAt?.toDate?.() || data.startedAt,
              completedAt: data.completedAt?.toDate?.() || data.completedAt
            };
          });
          setSessions(sessionsList);
          setStats(calcStats(sessionsList, tasks));
          setSessionsLoaded(true);
        }, (err) => {
          console.error('Sessions listener error:', err);
          setSessionsLoaded(true);
        });
      unsubSessionsRef.current = unsub;

      // Mark as fetched to prevent re-fetching on tab switches
      fetchedRef.current = true;
    } catch (err) {
      console.error('Error fetching project data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId]);

  // Save user testing config
  const saveConfig = useCallback(async (config) => {
    if (!userId || !projectId) return false;

    try {
      const db = firebase.firestore();
      await db
        .collection('user')
        .doc(userId)
        .collection('url')
        .doc(projectId)
        .update({
          userTesting: config,
          updatedAt: new Date()
        });

      // Keep pluginUserTesting mirror in sync so /ut-test/:id stays current
      await db.collection('pluginUserTesting').doc(projectId).set(
        {
          figmaUrl: config.prototypeUrl || '',
          userTesting: config,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );

      // Update local state
      setProject((prev) => ({
        ...prev,
        userTesting: config
      }));

      return true;
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err.message);
      return false;
    }
  }, [userId, projectId]);

  // Delete a single session
  const deleteSession = useCallback(async (sessionId) => {
    if (!userId || !projectId || !sessionId) return false;

    try {
      setDeleting(true);
      const db = firebase.firestore();

      await db
        .collection('user')
        .doc(userId)
        .collection('url')
        .doc(projectId)
        .collection('testingSessions')
        .doc(sessionId)
        .delete();

      // Remove from local state
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      // Update stats
      setStats((prev) => prev ? { ...prev, sessionCount: prev.sessionCount - 1 } : null);
      return true;
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.message);
      return false;
    } finally {
      setDeleting(false);
    }
  }, [userId, projectId]);

  // Delete multiple sessions (bulk delete)
  const deleteSessions = useCallback(async (sessionIds) => {
    if (!userId || !projectId || !sessionIds?.length) return false;

    try {
      setDeleting(true);
      const db = firebase.firestore();
      const batch = db.batch();

      sessionIds.forEach((sessionId) => {
        const ref = db
          .collection('user')
          .doc(userId)
          .collection('url')
          .doc(projectId)
          .collection('testingSessions')
          .doc(sessionId);
        batch.delete(ref);
      });

      await batch.commit();

      // Remove from local state
      setSessions((prev) => prev.filter((s) => !sessionIds.includes(s.id)));
      // Update stats
      setStats((prev) => prev ? { ...prev, sessionCount: prev.sessionCount - sessionIds.length } : null);
      return true;
    } catch (err) {
      console.error('Error deleting sessions:', err);
      setError(err.message);
      return false;
    } finally {
      setDeleting(false);
    }
  }, [userId, projectId]);

  useEffect(() => {
    fetchProjectData();
    return () => {
      if (unsubSessionsRef.current) {
        unsubSessionsRef.current();
        unsubSessionsRef.current = null;
      }
      if (unsubInsightsRef.current) {
        unsubInsightsRef.current();
        unsubInsightsRef.current = null;
      }
    };
  }, [fetchProjectData]);

  return {
    project,
    stats,
    synthesis,
    insightsRun,
    sessions,
    sessionsLoaded,
    loading,
    error,
    deleting,
    saveConfig,
    deleteSession,
    deleteSessions,
    refetch: () => fetchProjectData(true) // Force refetch
  };
}

export default useProjectData;
